import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Callout,
  EmptyState,
  Icon,
  Pagination,
  RowsSkeletonLoader,
  useSidePanel,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/ThemeLottie'
import { PageHeader } from 'components/header/PageHeader'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useGetPaymentLimits } from 'hooks/queries/useGetPaymentLimits'
import { useSearchablePagination } from 'hooks/useSearchablePagination'
import { PaymentLimitSheetType, type Limit } from 'types/paymentLimits'
import mockPaymentLimits from '../../../__mocks__/PaymentLimits/paymentLimits.json'
import { PaymentLimitCard } from './components/PaymentLimitCard'
import { PaymentLimitSheet } from './components/PaymentLimitSheet'
import { PaymentLimitSidePanel } from './components/PaymentLimitSidePanel'
import { groupLimitsByMethodAndCurrency } from './utils'

export const PaymentLimits = () => {
  const { t } = useTranslation()
  const { currentAccount } = useAccountsContext()
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useSearchablePagination(MIN_PAGINATION_SIZE)

  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false)
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false)
  const [selectedLimit, setSelectedLimit] = useState<Limit | null>(null)

  const {
    data: limitsData,
    isFetching,
    isError,
  } = useGetPaymentLimits({
    accountId: currentAccount?.reference,
    page,
    size: rowsPerPage,
  })

  // Will remove once the API is ready, for now it allows to test pagination and empty state scenarios without relying on the API response
  const mockLimits = useMemo(() => {
    const startIndex = page * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return (mockPaymentLimits.content as unknown as Limit[]).slice(startIndex, endIndex)
  }, [page, rowsPerPage])

  const limits = limitsData?.content ?? mockLimits
  const totalElements = limitsData?.totalElements ?? mockPaymentLimits.totalElements

  const groupedLimits = useMemo(() => groupLimitsByMethodAndCurrency(limits), [limits])

  const hasLimits = groupedLimits?.length > 0
  const showEmptyState = !isFetching && !isError && groupedLimits?.length === 0

  const {
    isSidePanelOpen,
    sidePanelData: selectedSidePanelLimit,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(limits)

  const handleEditLimit = useCallback((limit: Limit) => {
    setSelectedLimit(limit)
    setIsEditSheetOpen(true)
  }, [])

  const handleCloseEditSheet = useCallback(() => {
    setIsEditSheetOpen(false)
    setSelectedLimit(null)
  }, [])

  const handleViewDetails = useCallback(
    (limit: Limit) => {
      const index = limits?.findIndex(item => item?.ruleId === limit?.ruleId)
      if (index < 0) return
      updateSidePanel(index)
      setIsSidePanelOpen(true)
    },
    [limits, setIsSidePanelOpen, updateSidePanel]
  )

  return (
    <>
      <PageHeader
        description={t('paymentLimits.pageDescription')}
        renderRightSection={
          <Button
            testId="add-limit-button"
            iconElement={<Icon name="Plus" />}
            onClick={() => setIsAddSheetOpen(true)}
          >
            {t('paymentLimits.addLimit')}
          </Button>
        }
      />

      <Box paddingB={24}>
        <Callout>{t('paymentLimits.customerLimitsCallout')}</Callout>
      </Box>

      {isFetching && <RowsSkeletonLoader rows={3} />}

      {hasLimits && (
        <>
          <Box flex isFlexWrap gap={16}>
            {groupedLimits?.map(groupedLimit => (
              <Box
                key={`${groupedLimit.method}-${groupedLimit.currency}`}
                className="w-full md:w-[calc(50%-8px)]"
              >
                <PaymentLimitCard
                  groupedLimit={groupedLimit}
                  onEditLimit={handleEditLimit}
                  onViewDetails={handleViewDetails}
                />
              </Box>
            ))}
          </Box>

          {totalElements > rowsPerPage && (
            <Box paddingT={24}>
              <Pagination
                testId="payment-limits-pagination"
                page={page}
                rowsPerPage={rowsPerPage}
                total={totalElements}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </Box>
          )}
        </>
      )}

      {showEmptyState && (
        <EmptyState
          testId="payment-limits-empty-state"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('paymentLimits.emptyStateTitle')}
          description={t('paymentLimits.emptyStateDescription')}
        />
      )}

      <PaymentLimitSheet
        isOpen={isAddSheetOpen}
        type={PaymentLimitSheetType.ADD}
        onClose={() => setIsAddSheetOpen(false)}
      />

      <PaymentLimitSheet
        isOpen={isEditSheetOpen}
        type={PaymentLimitSheetType.EDIT}
        limit={selectedLimit}
        onClose={handleCloseEditSheet}
      />

      <PaymentLimitSidePanel
        isOpen={isSidePanelOpen}
        limit={selectedSidePanelLimit ?? null}
        onClose={() => setIsSidePanelOpen(false)}
      />
    </>
  )
}
